import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Tooltip,
  useColorModeValue,
  VStack
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { useCallback, useEffect, useState } from "react";
import {
  DELIVERY_API_ENDPOINT_V1,
  DELIVERY_API_ENDPOINT_V2
} from "../constants/api";
import { IEnvironmentClientResponse } from "../features/environment-clients/types";
import { usePreviewData } from "../features/views/api/getPreviewData";
import { IPreviewResponse } from "../features/views/types";
import useMixPanel from "../mixpanel/useMixPanel";
import useReactSelectStyles from "../styles/react-select-style";
import { DeliveryApiVersionTypes } from "../types/api";
import ISelectOption from "../types/selectInput";

const CurlEditor = ({
  environmentClients,
  id,
  url,
  handle,
  onPreviewData
}: {
  environmentClients: IEnvironmentClientResponse[] | null;
  id?: string;
  url?: string;
  handle?: string;
  onPreviewData?: (data: IPreviewResponse) => void;
}) => {
  const [selectedEnvironmentClient, setSelectedEnvironmentClient] =
    useState<ISelectOption>();
  const [curlCopied, setCurlCopied] = useState<boolean>(false);
  const [previewEnabled, setPreviewEnabled] = useState<boolean>(false);
  const [isCurlExecuted, setIsCurlExecuted] = useState<boolean>(false);
  const [currentEnvironmentClient, setCurrentEnvironmentClient] =
    useState<IEnvironmentClientResponse | null>(null);
  const mixpanel = useMixPanel();

  const reactSelectStyles = useReactSelectStyles();

  const environmentClientsOptions = useCallback((): ISelectOption[] => {
    if (!environmentClients) {
      return [];
    }

    return environmentClients.map(
      (item) =>
        ({
          label: item.name,
          value: item.id.clientGuid
        } as ISelectOption)
    );
  }, [environmentClients]);

  useEffect(() => {
    if (environmentClientsOptions) {
      setSelectedEnvironmentClient(environmentClientsOptions()[0]);
    }
  }, [environmentClientsOptions]);

  const deliveryApiVersionsOptions = (): ISelectOption[] => {
    return Object.keys(DeliveryApiVersionTypes).map((key) => ({
      label: key,
      value:
        DeliveryApiVersionTypes[key as keyof typeof DeliveryApiVersionTypes]
    }));
  };

  const [selectedDeliveryApiVersion, setSelectedDeliveryApiVersion] =
    useState<ISelectOption>(deliveryApiVersionsOptions()?.[1] ?? null);

  const getDeliveryApiVersion = () => {
    switch (selectedDeliveryApiVersion.value) {
      case DeliveryApiVersionTypes.V1:
        return DELIVERY_API_ENDPOINT_V1;
      case DeliveryApiVersionTypes.V2:
        return DELIVERY_API_ENDPOINT_V2;
      default:
        return DELIVERY_API_ENDPOINT_V2;
    }
  };

  useEffect(() => {
    if (selectedEnvironmentClient && environmentClients) {
      const environmentClient = environmentClients.find(
        (x) => x.id.clientGuid === selectedEnvironmentClient.value
      );
      setCurrentEnvironmentClient(environmentClient ?? null);
    } else {
      setCurrentEnvironmentClient(null);
    }
  }, [selectedEnvironmentClient, environmentClients]);

  const getCurlUrl = () => {
    if (!environmentClients?.length) {
      return "";
    }

    const newUrl = new URL(getDeliveryApiVersion());

    if (id) {
      newUrl.searchParams.append("id", id);
    }

    if (handle) {
      newUrl.searchParams.append("handle", handle);
    }

    if (url) {
      newUrl.searchParams.append("url", url);
    }

    return newUrl.toString();
  };

  const curl = () => {
    return `curl -L -X GET '${getCurlUrl()}' -H 'X-Api-Key: ${
      currentEnvironmentClient?.accessKey ?? "N/A"
    }'`;
  };

  const copyCurl = async (): Promise<void> => {
    await navigator.clipboard.writeText(curl());
    mixpanel.track("curlCopied");
    setCurlCopied(true);
    setTimeout(() => {
      setCurlCopied(false);
    }, 2000);
  };

  const previewHeaders = () => {
    return {
      "X-Api-Key": currentEnvironmentClient?.accessKey ?? "N/A"
    };
  };

  const { data } = usePreviewData(
    getCurlUrl(),
    previewHeaders(),
    previewEnabled
  );

  useEffect(() => {
    if (data && isCurlExecuted && onPreviewData) {
      onPreviewData(data);
      setIsCurlExecuted(false);
    }
  }, [data, isCurlExecuted, onPreviewData]);

  const executeCurl = () => {
    setPreviewEnabled(false); // Disable the previous query
    setTimeout(() => {
      setPreviewEnabled(true); // Enable the new query
      setIsCurlExecuted(true);
    }, 0);
  };

  return (
    <Box
      bg={useColorModeValue("gray.100", "gray.800")}
      p="8"
      position="relative"
      borderRadius="sm"
    >
      <VStack spacing={4}>
        <FormControl>
          <FormLabel color="gray.500" fontSize="xs">
            Environment client
          </FormLabel>
          <Select
            size="sm"
            useBasicStyles
            chakraStyles={reactSelectStyles}
            value={selectedEnvironmentClient}
            colorScheme="brand"
            onChange={(value) =>
              setSelectedEnvironmentClient(value as ISelectOption)
            }
            options={environmentClientsOptions()}
          />
        </FormControl>
        <FormControl>
          <FormLabel color="gray.500" fontSize="xs">
            Delivery API version
          </FormLabel>
          <Select
            size="sm"
            useBasicStyles
            chakraStyles={reactSelectStyles}
            value={selectedDeliveryApiVersion}
            colorScheme="brand"
            onChange={(value) =>
              setSelectedDeliveryApiVersion(value as ISelectOption)
            }
            options={deliveryApiVersionsOptions()}
          />
        </FormControl>
        <Tooltip
          label={curlCopied ? "Copied!" : "Copy to clipboard"}
          placement="top"
          closeOnClick={false}
        >
          <Textarea
            bg={useColorModeValue("white", "gray.700")}
            value={curl()}
            cursor="pointer"
            onClick={copyCurl}
            readOnly
            _hover={{
              opacity: 0.8
            }}
            _active={{
              top: "5px"
            }}
            height={"84px"}
            borderRadius="sm"
          />
        </Tooltip>
        <Button
          colorScheme="brand"
          size="sm"
          onClick={executeCurl}
          isDisabled={!selectedEnvironmentClient}
        >
          Execute CURL
        </Button>
      </VStack>
    </Box>
  );
};

export default CurlEditor;
